import { useCallback, useMemo } from 'react'
import useSWR from 'swr'

import { reconcileArtifactBody } from '@/components/artifact-editor/reconcileArtifactBody'
import {
  ArtifactTipView,
  artifactTipsApi,
} from '@/lib/api'

import { ThreadArtifactItem } from './collectThreadArtifacts'

function getResponseStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') {
    return null
  }
  const ax = error as { response?: { status?: number } }
  return typeof ax.response?.status === 'number' ? ax.response.status : null
}

export interface UseThreadArtifactTipsResult {
  tipsByIdentity: Map<string, ArtifactTipView>
  /** True when list GET succeeded (owner). 403 → false. */
  canEdit: boolean
  isLoading: boolean
  refresh: () => Promise<Array<ArtifactTipView> | undefined>
  displayBody: (artifactIdentity: string, machineBody: string) => string
  upsertLocalTip: (tip: ArtifactTipView) => void
  /** Resolve tip by living `publicId` first, then legacy identity. */
  tipForItem: (item: ThreadArtifactItem) => ArtifactTipView | null
}

/**
 * Load owner tip rows for a run. SWR-keyed so inline + expanded share one fetch.
 * Non-owners: GET 403 → `canEdit` false, empty map.
 *
 * Matching prefers living `publicId` as `artifactIdentity`, then exact `item.key`.
 * Slug fallbacks are intentionally omitted — they leaked brief #1 edits onto brief #2.
 */
export function useThreadArtifactTips(
  runToken: string | null | undefined,
): UseThreadArtifactTipsResult {
  const swrKey = runToken ? (['artifact-tips', runToken] as const) : null

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    ([, token]: readonly ['artifact-tips', string]) =>
      artifactTipsApi.list(token),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  )

  const tipsByIdentity = useMemo(() => {
    const map = new Map<string, ArtifactTipView>()
    for (const tip of data ?? []) {
      const existing = map.get(tip.artifactIdentity)
      if (!existing || tip.revision >= existing.revision) {
        map.set(tip.artifactIdentity, tip)
      }
    }
    return map
  }, [data])

  const status = getResponseStatus(error)
  const canEdit = Boolean(runToken) && data !== undefined && status !== 403 && !error

  const tipForItem = useCallback(
    (item: ThreadArtifactItem): ArtifactTipView | null => {
      if (item.publicId) {
        const byPublicId = tipsByIdentity.get(item.publicId)
        if (byPublicId) {
          return byPublicId
        }
      }
      return tipsByIdentity.get(item.key) ?? null
    },
    [tipsByIdentity],
  )

  const displayBody = useCallback(
    (artifactIdentity: string, machineBody: string) => {
      return reconcileArtifactBody(
        machineBody,
        tipsByIdentity.get(artifactIdentity),
      )
    },
    [tipsByIdentity],
  )

  const upsertLocalTip = useCallback(
    (tip: ArtifactTipView) => {
      void mutate(
        (current) => {
          const list = current ? [...current] : []
          const index = list.findIndex(
            (row) =>
              row.artifactIdentity === tip.artifactIdentity &&
              row.revision === tip.revision,
          )
          if (index >= 0) {
            list[index] = tip
          } else {
            list.push(tip)
          }
          return list
        },
        { revalidate: false },
      )
    },
    [mutate],
  )

  const refresh = useCallback(async () => {
    return mutate()
  }, [mutate])

  return {
    tipsByIdentity,
    canEdit,
    isLoading: Boolean(runToken) && isLoading,
    refresh,
    displayBody,
    upsertLocalTip,
    tipForItem,
  }
}
